import { useState, type ReactNode } from 'react'
import Drawer from './Drawer'
import { Notice } from './Toast'
import { NavLink, Navigate, Outlet, useLocation } from 'react-router'
import { Wordmark } from './Brand'
import { Icon, type IconName } from './icons'
import { ApiError } from './api/client'
import { useAuth } from './auth/useAuth'
import { statusLabel, statusTone } from './lib/format'

export function RequireAuth() {
  const { authed } = useAuth()
  return authed ? <Outlet /> : <Navigate to="/login" replace />
}

const NAV: { to: string; label: string; icon: IconName; roles: string[] }[] = [
  { to: '/dashboard', icon: 'dashboard', label: 'Dashboard', roles: ['ADMISSIONS_ADMIN', 'PANEL_REVIEWER', 'SUPER_ADMIN'] },
  { to: '/applications', icon: 'applications', label: 'Applications', roles: ['ADMISSIONS_ADMIN', 'PANEL_REVIEWER', 'SUPER_ADMIN'] },
  { to: '/results', icon: 'results', label: 'Results', roles: ['ADMISSIONS_ADMIN', 'PANEL_REVIEWER', 'SUPER_ADMIN'] },
  { to: '/campaigns', icon: 'campaigns', label: 'Campaigns', roles: ['ADMISSIONS_ADMIN', 'SUPER_ADMIN'] },
  { to: '/invites', icon: 'invites', label: 'Invites', roles: ['ADMISSIONS_ADMIN', 'SUPER_ADMIN'] },
  { to: '/plans', icon: 'schedule', label: 'Schedule', roles: ['ADMISSIONS_ADMIN', 'SUPER_ADMIN'] },
  { to: '/content', icon: 'content', label: 'Content', roles: ['CONTENT_ADMIN', 'SUPER_ADMIN'] },
  { to: '/banks', icon: 'upload', label: 'Question banks', roles: ['CONTENT_ADMIN', 'SUPER_ADMIN'] },
  { to: '/programs', icon: 'programs', label: 'Programs', roles: ['ADMISSIONS_ADMIN', 'SUPER_ADMIN'] },
  { to: '/careers', icon: 'careers', label: 'Careers', roles: ['CONTENT_ADMIN', 'ADMISSIONS_ADMIN', 'SUPER_ADMIN'] },
  { to: '/staff', icon: 'staff', label: 'Staff', roles: ['SUPER_ADMIN'] },
  { to: '/settings', icon: 'settings', label: 'Settings', roles: ['SUPER_ADMIN'] },
  { to: '/institutions', icon: 'institutions', label: 'Institutions', roles: ['PLATFORM_ADMIN'] },
]

export function Shell() {
  const { hasRole, signOut } = useAuth()
  const { pathname } = useLocation()
  return (
    <div className="layout">
      <nav className="side" aria-label="Main">
        <div className="brand"><Wordmark suffix="Admin" /></div>
        {NAV.filter((n) => hasRole(...n.roles)).map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? 'active' : '')}><Icon name={n.icon} />{n.label}</NavLink>
        ))}
        <div className="grow" />
        <NavLink to="/account"><Icon name="account" />Account</NavLink>
        <button type="button" onClick={signOut}>Sign out</button>
      </nav>
      <main className="main page-enter" key={pathname}><Outlet /></main>
    </div>
  )
}

/** Plain-language error; the machine code stays out of sight. */
export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof ApiError ? (error.code === 'NETWORK' ? error.message : error.status >= 500 ? 'Something went wrong on our side. Please try again.' : error.message) : 'Something went wrong. Please try again.'
  return <Notice tone="error">{message}</Notice>
}

export function StatusBadge({ status }: { status: string }) {
  return <span className={`badge ${statusTone(status)}`}>{statusLabel(status)}</span>
}

export function Pagination({ page, size, total, onPage }: { page: number; size: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / size))
  return (
    <div className="row" style={{ marginTop: 10 }}>
      <button type="button" disabled={page === 0} onClick={() => onPage(page - 1)}>Previous</button>
      <span className="muted" aria-live="polite">Page {page + 1} of {pages} · {total} total</span>
      <button type="button" disabled={page + 1 >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  )
}

/** Every sensitive change asks for a reason, which is recorded in the audit log. */
export function ReasonDialog({ title, description, confirmLabel, danger, busy, error, onCancel, onConfirm, children }: {
  title: string; description?: string; confirmLabel: string; danger?: boolean; busy: boolean; error: unknown
  onCancel: () => void; onConfirm: (reason: string) => void; children?: ReactNode
}) {
  const [reason, setReason] = useState('')
  return (
    <Drawer title={title} width={520} onClose={onCancel} onSubmit={() => { onConfirm(reason.trim()) }}
      footer={<><button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className={danger ? 'danger' : 'primary'} disabled={busy || !reason.trim()}>{busy ? 'Working…' : confirmLabel}</button></>}>
        {description && <p className="muted">{description}</p>}
        {children}
        <label className="field">Reason (kept in the audit log)
          <textarea required value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        <ErrorNotice error={error} />
    </Drawer>
  )
}
